import { i18n, initI18n } from '@hbs-us/ui';

import { readStoredLocale, setStoredLocale } from './lib/locale.js';
import enActivity from './locales/en/activity.json' with { type: 'json' };
import enClients from './locales/en/clients.json' with { type: 'json' };
import enCommon from './locales/en/common.json' with { type: 'json' };
import enDispatch from './locales/en/dispatch.json' with { type: 'json' };
import enEmployees from './locales/en/employees.json' with { type: 'json' };
import enEstimates from './locales/en/estimates.json' with { type: 'json' };
import enFleet from './locales/en/fleet.json' with { type: 'json' };
import enHome from './locales/en/home.json' with { type: 'json' };
import enInventory from './locales/en/inventory.json' with { type: 'json' };
import enInvoices from './locales/en/invoices.json' with { type: 'json' };
import enJobs from './locales/en/jobs.json' with { type: 'json' };
import enJoin from './locales/en/join.json' with { type: 'json' };
import enMonthlyBills from './locales/en/monthlyBills.json' with { type: 'json' };
import enNav from './locales/en/nav.json' with { type: 'json' };
import enNotifications from './locales/en/notifications.json' with { type: 'json' };
import enPayroll from './locales/en/payroll.json' with { type: 'json' };
import enQc from './locales/en/qc.json' with { type: 'json' };
import enReports from './locales/en/reports.json' with { type: 'json' };
import enServiceAgreements from './locales/en/serviceAgreements.json' with { type: 'json' };
import enServiceTitan from './locales/en/servicetitan.json' with { type: 'json' };
import enSettings from './locales/en/settings.json' with { type: 'json' };
import enSupport from './locales/en/support.json' with { type: 'json' };
import enTimesheets from './locales/en/timesheets.json' with { type: 'json' };
import enUsers from './locales/en/users.json' with { type: 'json' };
import esActivity from './locales/es/activity.json' with { type: 'json' };
import esClients from './locales/es/clients.json' with { type: 'json' };
import esCommon from './locales/es/common.json' with { type: 'json' };
import esDispatch from './locales/es/dispatch.json' with { type: 'json' };
import esEmployees from './locales/es/employees.json' with { type: 'json' };
import esEstimates from './locales/es/estimates.json' with { type: 'json' };
import esFleet from './locales/es/fleet.json' with { type: 'json' };
import esHome from './locales/es/home.json' with { type: 'json' };
import esInventory from './locales/es/inventory.json' with { type: 'json' };
import esInvoices from './locales/es/invoices.json' with { type: 'json' };
import esJobs from './locales/es/jobs.json' with { type: 'json' };
import esJoin from './locales/es/join.json' with { type: 'json' };
import esMonthlyBills from './locales/es/monthlyBills.json' with { type: 'json' };
import esNav from './locales/es/nav.json' with { type: 'json' };
import esNotifications from './locales/es/notifications.json' with { type: 'json' };
import esPayroll from './locales/es/payroll.json' with { type: 'json' };
import esQc from './locales/es/qc.json' with { type: 'json' };
import esReports from './locales/es/reports.json' with { type: 'json' };
import esServiceAgreements from './locales/es/serviceAgreements.json' with { type: 'json' };
import esServiceTitan from './locales/es/servicetitan.json' with { type: 'json' };
import esSettings from './locales/es/settings.json' with { type: 'json' };
import esSupport from './locales/es/support.json' with { type: 'json' };
import esTimesheets from './locales/es/timesheets.json' with { type: 'json' };
import esUsers from './locales/es/users.json' with { type: 'json' };

/** App namespaces beyond the shared `common` (so t('nav:…') resolves). */
const APP_NAMESPACES = [
  'common',
  'nav',
  'clients',
  'home',
  'reports',
  'notifications',
  'invoices',
  'estimates',
  'employees',
  'inventory',
  'fleet',
  'jobs',
  'join',
  'dispatch',
  'serviceAgreements',
  'servicetitan',
  'monthlyBills',
  'qc',
  'timesheets',
  'payroll',
  'users',
  'settings',
  'support',
  'activity',
] as const;

/**
 * Initialise the shared i18next instance with the dashboard's app namespaces.
 *
 * `@hbs-us/ui` `initI18n` deep-merges `extraResources` onto its own
 * { en:{common}, es:{common} } base (fallbackLng 'en'). We extend `common`
 * with the shell's error/session/loading strings, add a `nav` namespace
 * (sidebar labels + ComingSoon copy), and a `reports` namespace (the reports
 * room — Plan 04).
 *
 * Every user-facing string is t()-keyed in BOTH EN and ES (I18N-05).
 */
export function setupI18n() {
  // Read the persisted locale (allowlist-validated, defaults to `en`) so the
  // dashboard boots in the language the user last chose (I18N-05 parity with
  // the appearance selector, which already persists).
  const initialLocale = readStoredLocale();
  const instance = initI18n({
    lng: initialLocale,
    extraResources: {
      en: {
        common: enCommon,
        nav: enNav,
        clients: enClients,
        home: enHome,
        reports: enReports,
        notifications: enNotifications,
        invoices: enInvoices,
        estimates: enEstimates,
        employees: enEmployees,
        inventory: enInventory,
        fleet: enFleet,
        jobs: enJobs,
        join: enJoin,
        dispatch: enDispatch,
        serviceAgreements: enServiceAgreements,
        servicetitan: enServiceTitan,
        monthlyBills: enMonthlyBills,
        qc: enQc,
        timesheets: enTimesheets,
        payroll: enPayroll,
        users: enUsers,
        settings: enSettings,
        support: enSupport,
        activity: enActivity,
      },
      es: {
        common: esCommon,
        nav: esNav,
        clients: esClients,
        home: esHome,
        reports: esReports,
        notifications: esNotifications,
        invoices: esInvoices,
        estimates: esEstimates,
        employees: esEmployees,
        inventory: esInventory,
        fleet: esFleet,
        jobs: esJobs,
        join: esJoin,
        dispatch: esDispatch,
        serviceAgreements: esServiceAgreements,
        servicetitan: esServiceTitan,
        monthlyBills: esMonthlyBills,
        qc: esQc,
        timesheets: esTimesheets,
        payroll: esPayroll,
        users: esUsers,
        settings: esSettings,
        support: esSupport,
        activity: esActivity,
      },
    },
  });
  // initI18n only registers `common` in its `ns` list; load the app namespaces
  // so t('nav:…') resolves without a missing-namespace warning.
  for (const ns of APP_NAMESPACES) {
    if (!instance.hasResourceBundle('en', ns)) continue;
    instance.loadNamespaces(ns);
  }
  // initI18n only runs i18next `.init` once (guarded by `isInitialized`), so
  // re-activate the persisted locale explicitly — this makes the stored choice
  // win even when i18next was already initialised (HMR / re-entry).
  void instance.changeLanguage(initialLocale);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = initialLocale;
  }
  return instance;
}

/** Switch the active language, persist it, and reflect it on <html lang> (A11Y-01). */
export function setLanguage(lang: 'en' | 'es') {
  setStoredLocale(lang);
  void i18n.changeLanguage(lang);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}
